import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  appendOwnMessage,
  applyRoomMessage,
  type ChatMessage,
  HEARTBEAT_MS,
  type LineupEntry,
  nameHash,
  type OutputOf,
  reactionCount,
} from '@going-once/client';
import { cssVar } from '@going-once/tokens';
import { bidsChannel, type RealtimeMessage, roomChannel } from '@going-once/contracts/channels';
import { api } from '../lib/api';
import { isSignedIn, signIn } from '../lib/auth';
import { useChannel } from '../lib/realtime';
import { useStage } from '../lib/stage';
import { describeError } from '../lib/useAsync';
import { Box, Card, Pressable, Stack, Text } from '../ui/primitives';
import { ErrorNote, Loading } from '../ui/state';
import { type AuctionView, Countdown, MaxBidSheet, nextBidAction, type Position, StatusBanner } from './Auction';
import { formatCents } from './Feed';

type Show = OutputOf<'shows.show.get'>['show'];

/**
 * S-A4 — the live bid panel over video.
 *
 * **The panel is the timed screen's anatomy, re-skinned** (design/auction-surfaces.md):
 * the same status banner, the same max-bid sheet, the same server-anchored countdown,
 * imported rather than rewritten. What changes is the layout — the video is the screen
 * and the auction is an overlay on it — and the clock, which on a live lot is seconds
 * rather than days.
 *
 * **The channel drives it and the poll backs it up** (D50). A message on
 * `shows/{showId}/bids` is a nudge — it says something moved, and this screen refetches
 * rather than trusting the message's contents, because a realtime payload is deliberately
 * smaller than what a viewer is allowed to see. The poll underneath drops from two seconds
 * to fifteen once messages are actually arriving, and carries the whole load when they are
 * not: a dropped socket, a slept tab, or a deployment with no realtime stack at all.
 *
 * **Guests watch, guests do not bid.** Watching needs a viewer token, which the API
 * mints for a guest principal; bidding needs an account and a card, and the screen asks
 * for the first at the point the person tries.
 */
export function LiveShow({ showId, onBack }: { showId: string; onBack: () => void }) {
  const [show, setShow] = useState<Show>();
  const [lineup, setLineup] = useState<LineupEntry[]>([]);
  const [viewerToken, setViewerToken] = useState<string>();
  const [lot, setLot] = useState<AuctionView>();
  const [done, setDone] = useState<AuctionView[]>([]);
  const [position, setPosition] = useState<Position>();
  const [error, setError] = useState<{ message: string; code?: string; requestId?: string }>();
  const [sheetOpen, setSheetOpen] = useState(false);
  /** serverNow − Date.now() at the last read: every countdown is drawn through this. */
  const skew = useRef(0);
  /**
   * Whether we already hold a viewer token, readable from inside the poll without making
   * the poll depend on it.
   *
   * This is load-bearing: the show is re-read every fifteen seconds, and minting a token
   * on each pass would change the token on every pass — which re-runs `useStage`, which
   * leaves the stage and rejoins it. The video would drop every fifteen seconds, forever.
   * One token per session; it lasts an hour, and expiry is handled by re-mounting.
   */
  const hasToken = useRef(false);

  const signedIn = isSignedIn();
  const stage = useStage({ token: viewerToken, publish: false });

  /**
   * The realtime channel, and what it is *for*.
   *
   * **It does not carry the state — it says when to go and get it.** A message is a nudge
   * with just enough in it to render, the poll below is what produces the authoritative
   * view, and the two meet here: an arriving message refetches immediately rather than
   * waiting for the next tick. That is also what makes a missed message survivable, which
   * it has to be, because the publisher is at-most-once by design (D50).
   *
   * The sequence check is the other half. The change count is gapless from the engine, so a jump
   * means a message never arrived — and the answer is the same refetch, just for a
   * different reason (runtime-model §3.4).
   */
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [watching, setWatching] = useState(0);
  const [reactions, setReactions] = useState(0);

  /**
   * Reactions this client sent, so its own echo does not double them.
   *
   * A tap animates immediately — a heart that waits for a round trip feels broken — and
   * the same reaction then arrives back on the channel like everyone else's. Without this
   * every one of your own hearts appeared twice, which is what "they keep coming after the
   * first click" was.
   */
  const ownReactions = useRef(new Set<string>());

  /**
   * The last sequence seen **per auction**. `sequence` is the engine's change count, which
   * restarts at every lot — a single counter for the show dropped every bid of the second
   * lot as "already seen" and left the panel on its poll for the rest of the show.
   */
  const lastSequence = useRef(new Map<string, number>());
  const refetch = useRef<() => void>(() => {});
  const channel = useChannel(show?.state === 'live' ? bidsChannel(showId) : undefined, (message: RealtimeMessage) => {
    if (message.sequence !== undefined) {
      const key = message.auctionId ?? '';
      if (message.sequence <= (lastSequence.current.get(key) ?? 0)) return; // already seen, or out of order
      lastSequence.current.set(key, message.sequence);
    }
    refetch.current();
  });

  /**
   * The room, on its own channel.
   *
   * **Applied directly rather than by refetching**, unlike a bid. A chat message is
   * complete in the message — there is nothing more the server would tell us — whereas a
   * bid is a nudge to re-read state the payload is deliberately smaller than. The split is
   * the reason there are two channels at all: this one is chatty and cheap, that one is
   * authoritative and must not be trusted.
   */
  useChannel(show?.state === 'live' ? roomChannel(showId) : undefined, (message: RealtimeMessage) => {
    setChat((current) => applyRoomMessage(current, message));
    const hearts = reactionCount(message, ownReactions.current);
    if (hearts > 0) setReactions((current) => current + hearts);
  });

  // The show, and — once it is live — a viewer token. The token is minted per viewer and
  // expires, so it is fetched here rather than cached anywhere.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { show: fresh, lineup: lots } = await api().query('shows.show.get', { showId });
        if (cancelled) return;
        skew.current = fresh.serverNowMs - Date.now();
        setShow(fresh);
        setLineup(lots);
        setError(undefined);

        if (fresh.state === 'live' && !hasToken.current) {
          hasToken.current = true;
          const joined = await api().command('shows.viewer.join', { showId }, {});
          if (cancelled) return;
          setViewerToken(joined.viewer.token);
        } else if (fresh.state !== 'live') {
          // The show came off air: drop the credential rather than hold one for a stage
          // that no longer exists.
          hasToken.current = false;
          setViewerToken(undefined);
        }
      } catch (caught) {
        if (!cancelled) setError(describeError(caught));
      }
    }
    void load();
    const timer = setInterval(load, 15_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [showId]);

  /**
   * Arriving into a room mid-conversation, and staying counted in it.
   *
   * **The history read is what makes the subscription's at-most-once delivery survivable**
   * (D50): a viewer who opens a show twenty minutes in, or whose socket dropped, gets the
   * last of the conversation from the store rather than an empty room that fills up only
   * if somebody happens to type next.
   *
   * The heartbeat is presence. It runs at half the server's bucket so nobody flickers out
   * of the count between beats, and **stopping is how you leave** — there is no goodbye to
   * send, which is exactly why the count is a bucket that expires rather than a number
   * somebody has to remember to decrement.
   */
  useEffect(() => {
    if (show?.state !== 'live') return;
    let cancelled = false;

    void api()
      .query('shows.chat.history', { showId, limit: 50 })
      .then((result) => {
        if (!cancelled) setChat(result.messages.filter((message) => !message.removed));
      })
      // An empty rail is a fine failure here: the room fills from the channel either way.
      .catch(() => {});

    const beat = async () => {
      try {
        const { watching: count } = await api().command('shows.presence.heartbeat', { showId }, {});
        if (!cancelled) setWatching(count);
      } catch {
        // Presence is a label. It is never a gate, never an input to money, and not worth
        // an error banner over moving video.
      }
    };
    void beat();
    const timer = setInterval(beat, HEARTBEAT_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [showId, show?.state]);

  // The lot on the block. Two seconds, because this is the read that delivers the moment
  // the hammer falls — and it is what settles an expired lot today (D43).
  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const current = await api().query('auctions.auction.current', { showId, limit: 12 });
        if (cancelled) return;
        const running = current.live[0];
        skew.current = (running?.serverNowMs ?? current.done[0]?.serverNowMs ?? Date.now()) - Date.now();
        setLot(running);
        setDone(current.done);
        if (signedIn && running) {
          const mine = await api().query('auctions.bid.position', { auctionId: running.auctionId });
          if (!cancelled) setPosition(mine.position);
        } else if (!running) {
          setPosition(undefined);
        }
      } catch {
        // A failed poll is the next poll's problem. An error banner that appears and
        // disappears every two seconds is worse than a stale number.
      }
    }
    refetch.current = () => void poll();
    void poll();
    /**
     * **Two seconds without the channel, fifteen with it.**
     *
     * The poll is the backstop, not the mechanism: with a live subscription it exists to
     * catch a dropped socket and a tab that slept, and at two seconds it would be doing
     * the subscription's work at the subscription's cost. Without one it is the whole
     * mechanism and has to keep the pace of the room. `live` is "a message actually
     * arrived", not "a socket opened" — an open socket that never delivers is exactly the
     * failure this must not mistake for health.
     */
    const timer = setInterval(poll, channel.live ? 15_000 : 2_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [showId, signedIn, channel.live]);

  useEffect(() => {
    if (!show) return;
    document.title = `${show.title} · Going Once`;
    return () => {
      document.title = 'Going Once';
    };
  }, [show]);

  if (error && !show) return <ErrorNote error={error} />;
  if (!show) return <Loading what="the show" />;

  return (
    <div style={{ background: cssVar('color.bg.base'), minHeight: '100dvh' }}>
      {/*
        **The frame is the screen** (frame 6). The artboard has no app chrome at all: the
        video is edge to edge and the only way out is the ✕ over it. A navbar above a
        letterboxed player is a web page showing you a video; this is the product.

        `100dvh` rather than `100vh` — mobile browsers count their collapsing address bar
        in `vh`, which puts the bid button under it at exactly the moment somebody reaches
        for it.
      */}
      <Box
        style={{
          position: 'relative',
          height: '100dvh', // token-exempt: viewport-relative, and dvh is the mobile-correct one
          width: '100%',
          overflow: 'hidden',
          background: cssVar('gradient.hero'),
        }}
      >
        <Frame
          session={stage}
          show={show}
          chat={chat}
          watching={watching}
          reactions={reactions}
          onSay={async (body: string) => {
            const { message } = await api().command('shows.chat.post', { showId, body }, {});
            // Optimistic only in the sense that it does not wait for the round trip back
            // through the channel — the message is already stored by the time this runs.
            setChat((current) => appendOwnMessage(current, message));
          }}
          onReact={() => {
            setReactions((current) => current + 1);
            // The nonce is what lets our own echo be recognized when it comes back on the
            // channel. It was never sent, so every heart you tapped arrived twice.
            const nonce = crypto.randomUUID();
            ownReactions.current.add(nonce);
            void api()
              .command('shows.reaction.send', { showId, count: 1, nonce }, {})
              .catch(() => ownReactions.current.delete(nonce));
          }}
          lot={lot}
          position={position}
          signedIn={signedIn}
          skewMs={skew.current}
          onLeave={onBack}
          onBid={() => (signedIn ? setSheetOpen(true) : void signIn())}
        />
      </Box>

      {/*
        Below the fold. Scrolling past a live show is a deliberate choice rather than a
        fallback: the lineup, the sold lots and the sentence explaining proxy bidding are
        worth having, and they are worth nothing at the cost of the frame.
      */}
      <Stack
        gap="lg"
        style={{ maxWidth: cssVar('size.phone'), width: '100%', margin: '0 auto', padding: cssVar('space.lg') }}
      >
        {lot ? <StatusBanner auction={lot} position={position} signedIn={signedIn} /> : null}
        {done.length > 0 ? <Sold done={done} /> : null}
        {lineup.length > 0 ? <Lineup lineup={lineup} /> : null}
      </Stack>

      {sheetOpen && lot ? (
        <MaxBidSheet
          auction={lot}
          position={position}
          skewMs={skew.current}
          onDismiss={() => setSheetOpen(false)}
          onPlaced={(auction, mine) => {
            setLot(auction);
            setPosition(mine);
            setSheetOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * S-A4, frame 6 — built from the artboard rather than from the prose extraction of it.
 *
 * The written spec got the panel's contents right and the *screen* wrong. What the
 * artboard actually shows: video edge to edge with no app chrome, a seller pill and an ✕
 * floating on it, and the bid panel inset near the bottom. Everything else — chat over the
 * lower third, a follow button, a viewer count, a like button — is real in the design and
 * unbuilt here, so the frame is composed as if those will arrive rather than spread out
 * to fill the space they will occupy.
 */
function Frame({
  session,
  show,
  chat,
  watching,
  reactions,
  lot,
  position,
  signedIn,
  skewMs,
  onLeave,
  onBid,
  onSay,
  onReact,
}: {
  session: ReturnType<typeof useStage>;
  show: Show;
  chat: ChatMessage[];
  watching: number;
  reactions: number;
  lot: AuctionView | undefined;
  position: Position | undefined;
  signedIn: boolean;
  skewMs: number;
  onLeave: () => void;
  onBid: () => void;
  onSay: (body: string) => Promise<void>;
  onReact: () => void;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const stream = session.remote[0]?.stream;
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (ref.current && stream) ref.current.srcObject = stream;
  }, [stream]);

  return (
    <>
      {/* Something true to look at while the stage connects, rather than a flat gradient
          that reads as a broken player. */}
      {show.coverMediaKey && !stream ? <img src={api().mediaUrl(show.coverMediaKey)} alt="" style={FILL} /> : null}

      <video ref={ref} autoPlay playsInline muted={muted} style={{ ...FILL, display: stream ? 'block' : 'none' }} />

      <div
        style={{
          ...FILL,
          background: `linear-gradient(to bottom, ${cssVar('color.overlay.scrim')}, transparent 22%, transparent 42%, ${cssVar('color.overlay.scrim')})`,
          pointerEvents: 'none',
        }}
      />

      {/* Top: who is selling, and the way out. */}
      <div
        style={{
          position: 'absolute',
          top: cssVar('space.md'),
          left: cssVar('space.md'),
          right: cssVar('space.md'),
          display: 'flex',
          alignItems: 'center',
          gap: cssVar('space.sm'),
        }}
      >
        <SellerPill show={show} watching={watching} />
        <div style={{ flex: 1 }} />
        {stream ? (
          <RoundButton label={muted ? 'Turn sound on' : 'Mute'} onPress={() => setMuted((v) => !v)}>
            {muted ? '🔇' : '🔊'}
          </RoundButton>
        ) : null}
        {/* The artboard's only exit. No back link, no navbar — leaving a live show is one
            deliberate gesture, in the corner your thumb is not resting on. */}
        <RoundButton label="Leave the show" onPress={onLeave}>
          ✕
        </RoundButton>
      </div>

      {!stream ? (
        <Stack gap="xs" align="center" justify="center" style={{ height: '100%', padding: cssVar('space.lg') }}>
          {/* Never a silent black rectangle: that is indistinguishable from a broken
              build, a refused camera and a show that has not started. */}
          <Text tone="overlay.fg.muted">
            {show.state !== 'live'
              ? 'Not broadcasting'
              : session.status === 'error'
                ? (session.error ?? 'The video connection failed')
                : session.status === 'connected'
                  ? 'Waiting for the seller’s camera'
                  : 'Connecting to the show'}
          </Text>
        </Stack>
      ) : null}

      {/*
        The bottom third, in the artboard's order: chat over the video, the panel, then the
        composer. Stacked in one column so the panel keeps its place when the rail is empty
        — which it is, today.
      */}
      <div
        style={{
          position: 'absolute',
          left: cssVar('space.md'),
          right: cssVar('space.md'),
          bottom: cssVar('space.lg'),
          display: 'flex',
          flexDirection: 'column',
          gap: cssVar('space.sm'),
        }}
      >
        <ChatRail messages={chat} />
        {lot ? (
          <Panel lot={lot} position={position} signedIn={signedIn} skewMs={skewMs} onBid={onBid} />
        ) : (
          <Box radius="lg" padding="md" style={{ background: cssVar('color.overlay.scrim') }}>
            <Text tone="overlay.fg.muted">
              {show.state === 'live' ? 'Between lots — the next one is coming up' : 'Nothing on the block'}
            </Text>
          </Box>
        )}
        <ChatBar signedIn={signedIn} onSay={onSay} onReact={onReact} />
      </div>

      <Hearts reactions={reactions} />
    </>
  );
}

/**
 * The room, over the lower third — the artboard's chat rail.
 *
 * **Bottom-anchored and never scrolled by us.** A rail over video is a glance, not a
 * transcript: it shows the last few things said and lets older ones go. Anything further
 * back is `shows.chat.history`, which is also what fills this on arrival.
 *
 * Names are colored from the name itself so the same person is the same color to
 * everyone, with no palette to store and no server round trip — the artboard's look,
 * derived rather than assigned.
 */
function ChatRail({ messages }: { messages: ChatMessage[] }) {
  if (messages.length === 0) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: cssVar('space.xs'),
        maxHeight: '28vh',
        overflow: 'hidden',
        justifyContent: 'flex-end',
      }}
    >
      {messages.slice(-8).map((message) => (
        <div key={message.messageId} style={{ display: 'flex', gap: cssVar('space.xs'), alignItems: 'baseline' }}>
          <Text step="label" tone="overlay.fg">
            <span style={{ color: nameColor(message.displayName), fontWeight: cssVar('type.heading.weight') }}>
              {message.displayName}
            </span>
          </Text>
          <Text step="label" tone="overlay.fg">
            {message.body}
          </Text>
        </div>
      ))}
    </div>
  );
}

/**
 * A stable color per name, from the palette the design already has.
 *
 * Derived rather than stored: the same person is the same color on every device without a
 * lookup, and there is nothing to migrate when a name changes. Only the accent slots — no
 * raw hex, so a reskin moves these with everything else.
 */
const NAME_COLORS = ['color.accent.default', 'color.success', 'color.meta', 'color.warning', 'color.live'] as const;
function nameColor(name: string): string {
  return cssVar(NAME_COLORS[nameHash(name, NAME_COLORS.length)] as 'color.accent.default');
}

/**
 * The hearts themselves, rising off the button.
 *
 * **A reaction with no visual is not a reaction.** It is a button that sends a message
 * nobody sees — which is what shipped two days ago, and the thing that makes the feature
 * worth anything is precisely the part that was missing: seeing that other people are
 * here and enjoying it. That is the entire product value of a heart.
 *
 * Animated with `element.animate()` rather than CSS keyframes because each one needs its
 * own drift and duration — a column of identical hearts reads as a progress bar. Nothing
 * is stored, nothing is counted beyond the list, and a heart removes itself when it is
 * done; the state never grows.
 */
function Hearts({ reactions }: { reactions: number }) {
  const [floating, setFloating] = useState<{ id: string; seed: number }[]>([]);
  const seen = useRef(reactions);

  useEffect(() => {
    const added = reactions - seen.current;
    seen.current = reactions;
    if (added <= 0) return;

    // Capped: a burst of twenty from one tap-happy viewer should read as enthusiasm, not
    // fill the screen. The count is still whatever it is; this is only what is drawn.
    // An id and a drift seed: the id is the key, the seed is the path. Leaning on the
    // array index for either makes two hearts born together share a key and a trajectory.
    const born = Array.from({ length: Math.min(added, 6) }, () => ({ id: crypto.randomUUID(), seed: Math.random() }));
    setFloating((current) => [...current, ...born].slice(-24));

    /**
     * **No cleanup that cancels this.** The first version returned `clearTimeout`, so a
     * second burst inside 2.4s cancelled the first one's removal and those hearts stayed
     * on screen until something else happened to sweep them — which during an actual
     * flurry is never. Each burst owns its own timer and removes exactly its own ids.
     */
    setTimeout(() => {
      const ids = new Set<string>(born.map((heart) => heart.id));
      setFloating((current) => current.filter((heart) => !ids.has(heart.id)));
    }, 2_400);
  }, [reactions]);

  return (
    <div
      style={{
        position: 'absolute',
        right: cssVar('space.lg'),
        bottom: 0,
        width: cssVar('size.target.min'),
        height: '40vh',
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {floating.map(({ id, seed }) => (
        <span
          key={id}
          ref={(node) => {
            node?.animate(
              [
                { transform: 'translate(0, 0) scale(0.6)', opacity: 0 },
                { transform: `translate(${(seed - 0.5) * 48}px, -18vh) scale(1)`, opacity: 1, offset: 0.25 },
                { transform: `translate(${(seed - 0.5) * 96}px, -34vh) scale(0.9)`, opacity: 0 },
              ],
              { duration: 1_800 + seed * 900, easing: 'ease-out' },
            );
          }}
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            color: cssVar('color.accent.default'),
            fontSize: cssVar('type.heading.size'),
          }}
        >
          ♥
        </span>
      ))}
    </div>
  );
}

/**
 * The composer and the heart.
 *
 * **Both go through a capability** (D50) — the send is moderated, persisted, rate-limited
 * and audited before anyone sees it, and the heart is rate-limited for the ordinary reason
 * that a client publishing straight to the channel could fill the room.
 *
 * The heart counts up locally the instant it is tapped and does not wait for the round
 * trip: a reaction that lags is a reaction that feels broken, and it is stored nowhere, so
 * there is nothing to be wrong about.
 */
function ChatBar({
  signedIn,
  onSay,
  onReact,
}: {
  signedIn: boolean;
  onSay: (body: string) => Promise<void>;
  onReact: () => void;
}) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    // Cleared before the await: the room has already moved on by the time a round trip
    // finishes, and a field that stays full reads as a message that did not send.
    setDraft('');
    try {
      await onSay(body);
    } catch {
      // Put it back rather than losing what somebody typed.
      setDraft(body);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: cssVar('space.sm') }}>
      <input
        value={draft}
        disabled={!signedIn || busy}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') void send();
        }}
        maxLength={280}
        placeholder={signedIn ? 'Say something…' : 'Sign in to chat'}
        aria-label="Say something"
        style={{
          flex: 1,
          minWidth: 0,
          padding: `${cssVar('space.sm')} ${cssVar('space.md')}`,
          borderRadius: cssVar('radius.full'),
          border: 'none',
          background: cssVar('color.overlay.scrim'),
          backdropFilter: 'blur(12px)', // token-exempt: a rendering technique, not a design value
          color: cssVar('color.overlay.fg'),
          fontFamily: cssVar('family.base'),
          fontSize: cssVar('type.body.size'),
        }}
      />
      <button
        type="button"
        onClick={onReact}
        disabled={!signedIn}
        aria-label="Send a reaction"
        title={signedIn ? 'Send a reaction' : 'Sign in to react'}
        style={{
          display: 'grid',
          placeItems: 'center',
          width: cssVar('size.target.min'),
          height: cssVar('size.target.min'),
          flexShrink: 0,
          border: 'none',
          borderRadius: cssVar('radius.full'),
          background: cssVar('color.accent.default'),
          color: cssVar('color.fg.inverse'),
          fontSize: cssVar('type.body.size'),
          cursor: signedIn ? 'pointer' : 'default',
          boxShadow: cssVar('elevation.accentGlow'),
        }}
      >
        ♥
      </button>
    </div>
  );
}

/** Absolutely filling the frame — the video, the poster and the scrim all do it. */
const FILL = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
} as const;

/**
 * The seller, and how far through the show we are — the artboard's top-left pill.
 *
 * `FOLLOW` and the viewer count live here in the design and are **not built**: there is no
 * follow graph, and presence has to come from AppSync connections rather than the video
 * stage (D2), so a count here would be wrong by default. Left out rather than faked.
 */
function SellerPill({ show, watching }: { show: Show; watching: number }) {
  const initial = show.sellerDisplayName.trim().charAt(0).toUpperCase() || '?';
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: cssVar('space.sm'),
        padding: `${cssVar('space.xs')} ${cssVar('space.md')} ${cssVar('space.xs')} ${cssVar('space.xs')}`,
        borderRadius: cssVar('radius.full'),
        background: cssVar('color.overlay.scrim'),
        backdropFilter: 'blur(12px)', // token-exempt: a rendering technique, not a design value
        minWidth: 0,
      }}
    >
      <span
        style={{
          display: 'grid',
          placeItems: 'center',
          width: cssVar('size.target.min'),
          height: cssVar('size.target.min'),
          borderRadius: cssVar('radius.full'),
          background: cssVar('color.meta'),
          color: cssVar('color.fg.inverse'),
          fontFamily: cssVar('family.base'),
          fontWeight: cssVar('type.heading.weight'),
          flexShrink: 0,
        }}
      >
        {initial}
      </span>
      <span style={{ minWidth: 0 }}>
        <Stack gap="xs">
          <Text step="heading" tone="overlay.fg" truncate>
            {show.title}
          </Text>
          <Text step="label" tone="overlay.fg.muted" truncate>
            {/* The artboard reads "lot 14 / 60". We know the lineup's size but not which
                entry is on the block — that is a join the client already does in the
                studio and does not have here, so it says the half it knows. */}
            {show.sellerDisplayName} · {show.lineupSize} {show.lineupSize === 1 ? 'lot' : 'lots'}
            {/* **A real number at last** — from viewers of this show, never from video-stage
                participants (D2). Absent rather than zero until the first heartbeat lands,
                because "0 watching" while you are watching is worse than no number. */}
            {watching > 0 ? ` · ${watching} watching` : ''}
          </Text>
        </Stack>
      </span>
    </div>
  );
}

/** A circular control over video — the ✕ and the sound toggle share it. */
function RoundButton({ children, label, onPress }: { children: ReactNode; label: string; onPress: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onPress}
      style={{
        display: 'grid',
        placeItems: 'center',
        width: cssVar('size.target.min'),
        height: cssVar('size.target.min'),
        flexShrink: 0,
        border: 'none',
        borderRadius: cssVar('radius.full'),
        background: cssVar('color.overlay.scrim'),
        backdropFilter: 'blur(12px)', // token-exempt: a rendering technique, not a design value
        color: cssVar('color.overlay.fg'),
        fontFamily: cssVar('family.base'),
        fontSize: cssVar('type.body.size'),
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/**
 * The panel: lot, clock, the two numbers, the status line, the two buttons.
 *
 * **The clock is teal.** So is your max, and the status line when you are winning — the
 * color semantics put positive state and your own standing in teal, and the accent on
 * the thing the screen is asking about, which is the current bid. Rendering the timer in
 * plain white (the first pass) lost the one signal that says *this is still yours*.
 *
 * `EDIT MAX` sits beside the bid CTA and is shown whenever you could set a max, not only
 * once you have one: in the artboard it is always there, and a control that appears after
 * your first bid is a control you do not know exists when you need it.
 */
function Panel({
  lot,
  position,
  signedIn,
  skewMs,
  onBid,
}: {
  lot: AuctionView;
  position: Position | undefined;
  signedIn: boolean;
  skewMs: number;
  onBid: () => void;
}) {
  const closed = lot.status !== 'active';
  const cover = lot.mediaKeys[0];
  const leading = position?.standing === 'winning' || position?.standing === 'won';

  return (
    <Box
      radius="lg"
      padding="md"
      shadow="overlay"
      style={{
        background: cssVar('color.overlay.scrim'),
        backdropFilter: 'blur(12px)', // token-exempt: a rendering technique, not a design value
      }}
    >
      <Stack gap="md">
        <div style={{ display: 'flex', alignItems: 'center', gap: cssVar('space.sm') }}>
          {cover ? (
            <img
              src={api().mediaUrl(cover)}
              alt=""
              style={{
                width: cssVar('size.thumbnail.lot'),
                height: cssVar('size.thumbnail.lot'),
                objectFit: 'cover',
                borderRadius: cssVar('radius.md'),
                display: 'block',
                flexShrink: 0,
              }}
            />
          ) : null}
          {/* `minWidth: 0` is what makes the truncation work — without it the title
              refuses to shrink and drives itself under the clock. */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text step="heading" tone="overlay.fg" truncate>
              {lot.title}
            </Text>
          </div>
          <Stack gap="xs" align="end">
            <Text step="label" tone="overlay.fg.muted">
              TIME
            </Text>
            <Text step="countdown" tone="success">
              <Countdown endTimeMs={lot.endTimeMs} skewMs={skewMs} closed={closed} />
            </Text>
          </Stack>
        </div>

        <Stack direction="row" justify="between" gap="sm">
          <Stack gap="xs">
            <Text step="label" tone="overlay.fg.muted">
              CURRENT BID
            </Text>
            {/* The accent, because this is the number the screen is about. */}
            <Text step="display" tone="accent.default">
              {formatCents(lot.currentPriceCents)}
            </Text>
          </Stack>
          <Stack gap="xs" align="end">
            <Text step="label" tone="overlay.fg.muted">
              YOUR MAX
            </Text>
            <Text step="display" tone={position?.maxCents ? 'success' : 'overlay.fg.muted'}>
              {position?.maxCents ? formatCents(position.maxCents) : '—'}
            </Text>
          </Stack>
        </Stack>

        {/* One line, inside the panel, the way the artboard has it. The paragraph that
            explains proxy bidding to a first-time buyer is below the fold — it is worth
            saying once, not worth saying over moving video. */}
        <Text step="label" tone={leading ? 'success' : 'overlay.fg.muted'}>
          {closed
            ? lot.status === 'closed_won'
              ? `Sold to ${lot.winnerDisplayName ?? 'the winner'}`
              : 'Ended without a sale'
            : leading
              ? `You’re the high bidder — auto-bidding to ${formatCents(position?.maxCents ?? 0)}`
              : position?.standing === 'outbid'
                ? 'You’ve been outbid'
                : `Bidding in ${formatCents(lot.incrementCents)} steps`}
        </Text>

        {/*
          **One button, not the artboard's two.** Frame 6 pairs `EDIT MAX` with `BID $115`
          because they are different gestures there: one opens the sheet, the other commits
          the next increment in a tap. Here both opened the same sheet — one action wearing
          two labels — and the pair is worth restoring the day a true one-tap bid exists.

          The label comes from `nextBidAction`, shared with the sheet it opens, because
          this button said "Bid $16" to a bidder already winning at $15 with a max of $39
          and the sheet it opened said "Raise max to $40". You cannot outbid yourself.
        */}
        {closed ? null : (
          <Pressable variant="primary" size="large" grow onPress={onBid}>
            {signedIn ? nextBidAction(lot, position).label.toUpperCase() : 'Sign in to bid'}
          </Pressable>
        )}
      </Stack>
    </Box>
  );
}

function Sold({ done }: { done: AuctionView[] }) {
  return (
    <Stack gap="sm">
      <Text step="label" tone="fg.muted">
        ALREADY GONE
      </Text>
      {done.map((auction) => (
        <Card key={auction.auctionId} padding="sm">
          <Stack direction="row" justify="between" align="center" gap="sm" wrap>
            <Text>{auction.title}</Text>
            <Text tone="fg.muted">
              {auction.status === 'closed_won'
                ? `${formatCents(auction.clearingPriceCents ?? auction.currentPriceCents)} · ${auction.winnerDisplayName ?? 'sold'}`
                : 'No sale'}
            </Text>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

/** The plan, not the state: a lot appears in "already gone" once it has actually run. */
function Lineup({ lineup }: { lineup: LineupEntry[] }) {
  return (
    <Stack gap="sm">
      <Text step="label" tone="fg.muted">
        COMING UP
      </Text>
      {lineup.map((entry) => (
        <Card key={entry.entryId} padding="sm">
          <Stack direction="row" justify="between" align="center" gap="sm" wrap>
            <Text>
              {entry.position}. {entry.title}
            </Text>
            {entry.startPriceCents !== undefined ? (
              <Text tone="fg.muted">from {formatCents(entry.startPriceCents)}</Text>
            ) : null}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
